import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  Separator,
  Stack,
  Text,
} from '@chakra-ui/react';
import { LuSearch } from 'react-icons/lu';
import {
  DEFAULT_DUAL_LIST_LABELS,
  type DualListItem,
  type DualListLabels,
} from '@utils/dualListSelect';

export type { DualListItem, DualListLabels };

const normalize = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const highlightMatch = (label: string, query: string) => {
  if (!query.trim()) return label;
  const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${safeQuery})`, 'ig');
  const parts = label.split(regex);
  return parts.map((part, index) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <Box
        as="mark"
        key={`${part}-${index}`}
        bg="colorPalette.subtle"
        color="colorPalette.fg"
        px="0.5"
        rounded="sm"
      >
        {part}
      </Box>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
};

type SelectableRowProps = {
  label: string;
  onClick: () => void;
  trailing: React.ReactNode;
  query?: string;
  selected?: boolean;
};

const SelectableRow = ({ label, onClick, trailing, query = '', selected = false }: SelectableRowProps) => (
  <Flex
    align="center"
    justify="space-between"
    gap="3"
    px="3"
    py="2.5"
    borderRadius="xl"
    cursor="pointer"
    borderWidth="1px"
    borderColor={selected ? 'colorPalette.emphasized' : 'border.muted'}
    bg={selected ? 'colorPalette.subtle' : 'transparent'}
    _hover={{ bg: selected ? 'colorPalette.subtle' : 'bg.muted' }}
    onClick={onClick}
    transition="background 0.16s ease, border-color 0.16s ease"
  >
    <Text fontSize="sm" fontWeight="medium" truncate minW="0">
      {highlightMatch(label, query)}
    </Text>
    <Box flexShrink={0}>{trailing}</Box>
  </Flex>
);

const EmptyState = ({ title, description }: { title: string; description: string }) => (
  <Flex align="center" justify="center" minH="180px" px="6">
    <Stack gap="1" textAlign="center" maxW="320px">
      <Text fontWeight="semibold">{title}</Text>
      <Text fontSize="sm" color="fg.muted">{description}</Text>
    </Stack>
  </Flex>
);

type GroupedItems = { label: string; items: DualListItem[] };

const groupItems = (items: DualListItem[], defaultGroup: string): GroupedItems[] => {
  const map = new Map<string, DualListItem[]>();
  for (const item of items) {
    const key = item.group ?? defaultGroup;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }
  return Array.from(map.entries())
    .map(([label, grouped]) => ({
      label,
      items: [...grouped].sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export interface DualListPanelProps {
  items: DualListItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  labels?: DualListLabels;
  extraBadges?: React.ReactNode;
}

const DualListPanel = ({ items, selectedIds, onChange, labels, extraBadges }: DualListPanelProps) => {
  const l = { ...DEFAULT_DUAL_LIST_LABELS, ...labels };
  const [query, setQuery] = useState('');

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filteredItems = useMemo(() => {
    const q = normalize(query);
    const sorted = [...items].sort((a, b) => a.label.localeCompare(b.label));
    if (!q) return sorted;
    return sorted.filter((item) => normalize(item.label).includes(q));
  }, [items, query]);

  const filteredAvailable = useMemo(
    () => filteredItems.filter((item) => !selectedSet.has(item.id)),
    [filteredItems, selectedSet]
  );

  const selectedItems = useMemo(
    () => items.filter((item) => selectedSet.has(item.id)).sort((a, b) => a.label.localeCompare(b.label)),
    [items, selectedSet]
  );

  const groupedAvailable = useMemo(
    () => groupItems(filteredAvailable, l.defaultGroup),
    [filteredAvailable, l.defaultGroup]
  );
  const groupedSelected = useMemo(
    () => groupItems(selectedItems, l.defaultGroup),
    [selectedItems, l.defaultGroup]
  );

  const selectOne = (id: string) => {
    if (selectedSet.has(id)) return;
    onChange([...selectedIds, id]);
  };

  const removeOne = (id: string) =>
    onChange(selectedIds.filter((currentId) => currentId !== id));

  const selectAllFiltered = () => {
    const merged = new Set(selectedIds);
    for (const item of filteredAvailable) merged.add(item.id);
    onChange(Array.from(merged));
  };

  const clearSelection = () => onChange([]);

  return (
    <Stack gap="5" flex="1" minH="0">
      <Stack gap="1">
        <Text fontSize="sm" fontWeight="medium">Buscar</Text>
        <InputGroup startElement={<Icon as={LuSearch} color="fg.muted" />}>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={l.searchPlaceholder}
            variant="subtle"
            autoFocus
          />
        </InputGroup>
      </Stack>

      <HStack gap="3" flexWrap="wrap">
        <Badge variant="surface" rounded="full" px="3" py="1">
          {items.length} disponíveis
        </Badge>
        <Badge variant="surface" rounded="full" px="3" py="1">
          {selectedIds.length} selecionados
        </Badge>
        {extraBadges}
      </HStack>

      <Box borderRadius="md" overflow="hidden" bg="bg.muted" flex="1" minH="0" display="flex" flexDir="column">
        <Flex direction={{ base: 'column', lg: 'row' }} flex="1" minH="0">
          <Stack flex="1" minW="0" minH="0" gap="0">
            <Flex px="5" py="4" justify="space-between" align="center">
              <Stack gap="0">
                <Text fontWeight="semibold">{l.availableTitle}</Text>
                <Text fontSize="sm" color="fg.muted">{l.availableSubtitle}</Text>
              </Stack>
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAllFiltered}
                disabled={filteredAvailable.length === 0}
              >
                {l.selectAll}
              </Button>
            </Flex>

            <Separator opacity={0.4} />

            <Box px="3" py="3" overflow="auto" minH={{ base: '260px', lg: '0' }} flex="1">
              {groupedAvailable.length === 0 ? (
                <EmptyState title={l.emptyAvailableTitle} description={l.emptyAvailableDescription} />
              ) : (
                <Stack gap="4">
                  {groupedAvailable.map((group) => (
                    <Stack key={group.label} gap="2">
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="fg.muted"
                        textTransform="uppercase"
                        px="2"
                      >
                        {group.label}
                      </Text>
                      <Stack gap="1">
                        {group.items.map((item) => (
                          <SelectableRow
                            key={item.id}
                            label={item.label}
                            onClick={() => selectOne(item.id)}
                            trailing={
                              <Checkbox.Root checked={false} pointerEvents="none" readOnly>
                                <Checkbox.HiddenInput />
                                <Checkbox.Control />
                              </Checkbox.Root>
                            }
                            query={query}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>

          <Separator opacity={0.4} orientation={{ base: 'horizontal', lg: 'vertical' }} />

          <Stack flex="1" minW="0" minH="0" gap="0">
            <Flex px="5" py="4" justify="space-between" align="center">
              <Stack gap="0">
                <Text fontWeight="semibold">
                  {l.selectedTitle} ({selectedIds.length})
                </Text>
                <Text fontSize="sm" color="fg.muted">{l.selectedSubtitle}</Text>
              </Stack>
              <Button
                variant="ghost"
                colorPalette="red"
                size="sm"
                onClick={clearSelection}
                disabled={selectedIds.length === 0}
              >
                {l.clearAll}
              </Button>
            </Flex>

            <Separator opacity={0.4} />

            <Box px="3" py="3" overflowY="auto" minH={{ base: '220px', lg: '0' }} flex="1">
              {groupedSelected.length === 0 ? (
                <EmptyState title={l.emptySelectedTitle} description={l.emptySelectedDescription} />
              ) : (
                <Stack gap="4">
                  {groupedSelected.map((group) => (
                    <Stack key={group.label} gap="2">
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="fg.muted"
                        textTransform="uppercase"
                        px="2"
                      >
                        {group.label}
                      </Text>
                      <Stack gap="1">
                        {group.items.map((item) => (
                          <SelectableRow
                            key={item.id}
                            label={item.label}
                            onClick={() => removeOne(item.id)}
                            trailing={
                              <CloseButton
                                size="2xs"
                                aria-label={`Remover ${item.label}`}
                                pointerEvents="none"
                              />
                            }
                            query={query}
                            selected
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Flex>
      </Box>
    </Stack>
  );
};

export default DualListPanel;
